import { useGetEvent, useGetProfile } from '@sunrise-ui/api-client';
import {
  Loader,
  Modal,
  ProfileBasicInfo,
  ProfileImg,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { DateTime } from 'luxon';
import React from 'react';
import { Image, ScrollView, StyleSheet, View } from 'react-native';
import { QrCodeSvg } from 'react-native-qr-svg';
import { LogoIcon } from '../../assets/images';

type Props = {
  visible: boolean;
  hide: () => void;
  eventId: string;
  ticketToken?: string;
};

export function EventCheckInModal({
  visible,
  hide,
  eventId,
  ticketToken,
}: Props) {
  const { currentUserId } = useAuthStore();
  const styles = useDynamicStyles(createStyles);
  const { data: event, isLoading: isLoadingEvent } = useGetEvent(eventId);
  const { data: profile, isLoading: isLoadingProfile } =
    useGetProfile(currentUserId);

  const qrCodeValue = JSON.stringify({
    userId: currentUserId,
    token: ticketToken,
    eventId,
  });

  return (
    <Modal
      visible={visible}
      hide={hide}
      fullscreenOnBreakpoint={true}
      contentStyle={styles.modalContent}
      size="small"
      title="My Ticket"
    >
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {isLoadingEvent ? (
          <Loader />
        ) : (
          <>
            <View style={styles.profileSection}>
              <ProfileImg
                userId={currentUserId}
                imgSize="large"
                styleSize={75}
                isLoading={isLoadingProfile}
                profile={profile}
              />
              <ProfileBasicInfo
                firstName={profile?.first_name}
                lastName={profile?.last_name}
                pronouns={profile?.pronouns}
                headline={profile?.headline}
                isLoading={isLoadingProfile}
                size="medium"
                style={styles.profileInfo}
              />
            </View>

            <View style={styles.eventSection}>
              <TextReg style={styles.eventName}>{event?.name}</TextReg>
              {event?.start_date && (
                <TextReg style={styles.eventDate}>
                  {DateTime.fromISO(event.start_date).toLocaleString(
                    DateTime.DATETIME_FULL,
                  )}
                </TextReg>
              )}
            </View>

            <View style={styles.qrSection}>
              <View style={styles.qrCodeContainer}>
                <QrCodeSvg
                  value={qrCodeValue}
                  frameSize={250}
                  contentCells={8}
                  content={
                    <View style={styles.logoContainer}>
                      <Image
                        source={LogoIcon}
                        style={styles.logo}
                        resizeMode="contain"
                      />
                    </View>
                  }
                  contentBackgroundRectProps={{
                    fill: 'white',
                    rx: 8,
                    ry: 8,
                  }}
                />
              </View>
              <TextReg style={styles.qrLabel}>
                Use your QR code to check in and connect with others!
              </TextReg>
            </View>
          </>
        )}
      </ScrollView>
    </Modal>
  );
}

const createStyles = ({ isMobile }: WithResponsive<object>) =>
  StyleSheet.create({
    modalContent: {
      paddingTop: 0,
    },
    scrollContent: {
      alignItems: 'center',
      gap: 20,
    },
    profileSection: {
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'center',
      gap: 20,
      width: '100%',
      marginBottom: 30,
    },
    profileInfo: {
      flexGrow: 0,
    },
    eventSection: {
      alignItems: 'center',
      gap: 8,
      width: '100%',
    },
    eventName: {
      fontSize: isMobile ? 24 : 32,
      fontWeight: 'bold',
      textAlign: 'center',
    },
    eventDate: {
      fontSize: isMobile ? 14 : 18,
      opacity: 0.8,
      textAlign: 'center',
    },
    qrSection: {
      alignItems: 'center',
      gap: 16,
      width: '100%',
    },
    qrLabel: {
      fontSize: isMobile ? 16 : 20,
      textAlign: 'center',
    },
    qrCodeContainer: {
      backgroundColor: 'white',
      padding: 20,
      borderRadius: 16,
    },
    logoContainer: {
      width: 60,
      height: 60,
      padding: 6,
      alignItems: 'center',
      justifyContent: 'center',
    },
    logo: {
      width: '100%',
      height: '100%',
    },
  });
